/**
 * Bulk scorer assignment for a (date, surface).
 * Admin only — writes scorer_id on every playable game matching the criteria.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { X } from "lucide-react";

export default function BulkScorerModal({ event, officials = [], onClose, onApplied }) {
  const games = event.games || [];
  const dates = useMemo(() => {
    const s = new Set(games.filter((g) => !g.is_break && g.date).map((g) => g.date));
    return Array.from(s).sort();
  }, [games]);
  const [date, setDate] = useState(dates[0] || "");
  const surfacesForDate = useMemo(() => {
    const s = new Set(
      games
        .filter((g) => !g.is_break && g.date === date)
        .map((g) => String(g.surface || "")),
    );
    return Array.from(s).sort();
  }, [games, date]);
  const [surface, setSurface] = useState("");
  const [scorerId, setScorerId] = useState("");
  const [confirm, setConfirm] = useState(null); // { would_overwrite, matched }
  const [busy, setBusy] = useState(false);

  const scorers = officials.filter((o) => (o.roles || []).includes("scorer"));

  const submit = async (force) => {
    if (!date || !surface) {
      toast.error("Sélectionne une date et une surface");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/events/${event.id}/bulk-assign-scorer`,
        {
          date,
          surface,
          scorer_id: scorerId || null,
          force: !!force,
        },
      );
      if (data.would_overwrite && data.would_overwrite.length && !force) {
        setConfirm(data);
        return;
      }
      toast.success(`${data.updated}/${data.matched} partie(s) mise(s) à jour`);
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg border-2 border-black p-6 shadow-2xl" data-testid="bulk-scorer-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold uppercase tracking-wider">Assigner marqueur en bloc</h2>
          <button onClick={onClose} className="p-1 hover:bg-black/5" aria-label="Fermer">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm mb-4 text-[var(--muted-2)]">
          Applique le marqueur à toutes les parties d&apos;une (date, surface). Laisse « Aucun » pour détacher.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-xs">
            Date
            <select
              className="input-box w-full mt-1"
              value={date}
              onChange={(e) => { setDate(e.target.value); setSurface(""); setConfirm(null); }}
              data-testid="bulk-scorer-date"
            >
              {dates.map((d) => (<option key={d} value={d}>{d}</option>))}
            </select>
          </label>
          <label className="text-xs">
            Surface
            <select
              className="input-box w-full mt-1"
              value={surface}
              onChange={(e) => { setSurface(e.target.value); setConfirm(null); }}
              data-testid="bulk-scorer-surface"
            >
              <option value="">—</option>
              {surfacesForDate.map((s) => (<option key={s} value={s}>Surface {s}</option>))}
            </select>
          </label>
        </div>
        <label className="text-xs block mb-4">
          Marqueur
          <select
            className="input-box w-full mt-1"
            value={scorerId}
            onChange={(e) => { setScorerId(e.target.value); setConfirm(null); }}
            data-testid="bulk-scorer-select"
          >
            <option value="">— Aucun (détacher) —</option>
            {scorers.map((o) => (
              <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>
            ))}
          </select>
        </label>
        {confirm && (
          <div className="border-2 border-black bg-[var(--warn-bg,#fff)] p-3 mb-4 text-xs">
            <div className="font-bold uppercase mb-1">Écrasement détecté</div>
            <div>{confirm.would_overwrite.length} partie(s) ont déjà un marqueur différent :</div>
            <ul className="list-disc pl-4 mt-1">
              {confirm.would_overwrite.slice(0, 6).map((w) => (
                <li key={w.game_id}>{w.start_time} — {w.end_time}</li>
              ))}
              {confirm.would_overwrite.length > 6 && <li>… et {confirm.would_overwrite.length - 6} de plus</li>}
            </ul>
            <div className="mt-2">Confirmer l&apos;écrasement ?</div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" disabled={busy}>Annuler</button>
          {confirm ? (
            <button
              onClick={() => submit(true)}
              className="btn-primary"
              disabled={busy}
              data-testid="bulk-scorer-confirm-overwrite"
            >
              Forcer l&apos;écrasement
            </button>
          ) : (
            <button
              onClick={() => submit(false)}
              className="btn-primary"
              disabled={busy || !date || !surface}
              data-testid="bulk-scorer-submit"
            >
              Appliquer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
